import React, { useEffect, useState, useCallback } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import {
  getUserList,
  createUser,
  getNewUserEmailTemplateList,
  getRolesList,
  getStateOptions,
  deleteUser,
  updateUserRbacRole,
} from 'api'
import {
  Container,
  HeaderBar,
  DataGridTable,
  Blade,
  SubmitDialog,
  Toast as toast,
} from 'components'
import { DropdownFormField, InputFormField } from 'components/hookForm'
import { userManagementList } from 'constants/tableData'
import { getRoleOptions } from 'constants/roleOptions'
import { ObjectAnyKeys } from 'interfaces/Ihelper'
import styles from './usersList.module.scss'
import strings from 'strings'
import { isViewOnly, isInsuranceLogin, getRoleType } from 'tools/auth'
import { isRBACEnabled } from 'tools/featureFlags'
import { ROLE_TYPES } from 'rbac/roles'
import { reportException } from 'tools/logs'
import { emailValidator } from 'tools/validators'

const formatStateOptions = (data: any) => {
  return data?.map((item: any) => ({ id: item.name, value: item.name, label: item.name }))
}

const formatRolesOptions = (data: any) => {
  return data?.map((item: any) => ({
      id:item.name,
    value: item?.roleId,
    label: item?.roleName,
  }))
}

const formatEmailTemplateOptions = (data: any) => {
  return data?.map((item: any) => ({
      id:item.name,
    value: item?.carrierEmailTemplateId,
    label: item?.emailTemplateName,
  }))
}


const t = strings.USER_MANAGEMENT

const toastType: ObjectAnyKeys = {
  reject: 'error',
  hold: 'warning',
  deposit: 'success',
}

interface IUserManagementListProps {}

interface IBladeContentProps {}

const UserManagementList: React.FunctionComponent<IUserManagementListProps> =
  () => {
    const [showTemplateForm, setShowTemplateForm] = useState<boolean>(false)
    const [isEditForm, setIsEditForm] = useState(false)
    const [userListData, setUserListData] = useState([])
    const [selectedUser, setSelectedUser] = useState<string | number>('')
    const [loading, setLoading] = useState(false)
    const [btnLoader, setBtnLoader] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showRoleForm, setShowRoleForm] = useState<boolean>(false)
    const [roleFormUser, setRoleFormUser] = useState<any>(null)
    const [dropdownOptions, setDropdownOptions] = useState({
      emailTemplateOptions: [],
      rolesOptions: [],
      stateOptions: [],
    })

    const { emailTemplateColumn, actionList: baseActionList } = userManagementList

    const currentUserId = localStorage.getItem('userId')
    // ADMIN only. CARRIER_ADMIN was removed together with the API-side change that
    // tightened PUT/GET /v1/users/{id}/rbac-role to hasAnyRole('ADMIN') — see
    // CLAIMS_ONLY_PLAN.md §7.4 H3. The two must ship in the same release: leaving the
    // action visible here would offer a write the API now refuses, and the catch below
    // only calls reportException, so the dialog closes exactly as it does on success and
    // the user reads a silent 403 as "it worked".
    const isAdminRbac =
      isRBACEnabled() && isInsuranceLogin() && getRoleType() === ROLE_TYPES.ADMIN

    const actionList = isViewOnly()
      ? []
      : isAdminRbac
      ? [
          {
            name: 'switchRole',
            icon: 'icon_reconnect',
            iconSize: 40,
            selection: false,
            getLabel: () => 'Switch Role',
            isDisabled: (row: any) => String(row.userId) === String(currentUserId),
          },
          ...baseActionList,
        ]
      : baseActionList

    const methods = useForm<any>({
      reValidateMode: 'onChange',
    })

    const { handleSubmit, reset } = methods

    const loadUsersPage = useCallback(async () => {
      try {
        setLoading(true)
        await callUserList()
        const emailTemplateData: any = await getNewUserEmailTemplateList()
        const rolesListData: any = await getRolesList()
        const stateListData: any = await getStateOptions()
        setDropdownOptions({
          emailTemplateOptions: formatEmailTemplateOptions(
            emailTemplateData?.data
          ),
          rolesOptions: formatRolesOptions(rolesListData?.roleList),
          stateOptions: formatStateOptions(stateListData?.data),
        })
      } catch (error: any) {
        reportException(error)
      } finally {
        setLoading(false)
      }
    }, [])

    const callUserList = () => {
      setLoading(true)
      return getUserList()
        .then((res: any) => {
          setUserListData(res?.Users)
        })
        .catch((error: any) => {
          reportException(error)
        })
        .finally(() => setLoading(false))
    }

    useEffect(() => {
      loadUsersPage()
    }, [loadUsersPage])

    const handleClose = () => {
      setShowTemplateForm(false)
      reset({})
    }

    const handleActions = (_id: number | string, actionName: string) => {
      if (actionName === 'Edit') {
        const templateFormData = userListData.filter(
          (item: any) => item.userId === _id
        )
        setIsEditForm(true)
        reset(templateFormData[0])
        setShowTemplateForm(true)
      } else if (actionName === 'switchRole') {
        const user: any = userListData.find((item: any) => item.userId === _id)
        setRoleFormUser(user)
        reset({ roleType: user?.roleType })
        setShowRoleForm(true)
      } else {
        setSelectedUser(_id)
        setShowDeleteModal(true)
      }
    }

    const handleRoleFormClose = () => {
      setShowRoleForm(false)
      setRoleFormUser(null)
      reset({})
    }

    const onRoleSubmit = (data: any) => {
      if (!roleFormUser) return
      setBtnLoader(true)
      updateUserRbacRole(roleFormUser.userId, data.roleType)
        .then(() => {
          toast({
            id: 'changeRoleToast',
            subTitle: 'Role updated successfully',
          })
          callUserList()
        })
        .catch((error: any) => {
          reportException(error)
        })
        .finally(() => {
          setBtnLoader(false)
          setShowRoleForm(false)
          setRoleFormUser(null)
        })
    }

    const handleUserDelete = () => {
      setBtnLoader(true)
      deleteUser(selectedUser)
        .then((res) => {
          toast({
              id:"userManagementToastDelete",
            title: 'Deleted Successfully',
            subTitle: `User with ID: ${selectedUser} deleted successfully`,
          })
        })
        .catch((error: any) => {
          reportException(error)
        })
        .finally(() => {
          setBtnLoader(false)
          setShowDeleteModal(false)
          callUserList()
        })
    }

    const handleCheckboxSelection = (selections: any) => {
      console.log('checkbox', selections)
    }

    const handleSelectionActions = (
      actionName: string,
      selectionModel: any
    ) => {
      toast({
          id:"updatedRowsUserManagement",
        type: toastType[actionName],
        title: actionName,
        subTitle: `${selectionModel?.length} rows updated`,
      })
    }

    const loadPayments = (pageNumber: number, pageSize: number) => {
      console.log(`pagenumber: ${pageNumber}, pageSize: ${pageSize}`)
    }

    const onSubmit = (data: any) => {
      setBtnLoader(true)
      const companyId = localStorage.getItem('companyId')
      const userTypeId = localStorage.getItem('userTypeId')
      const payLoad = {
        userId: data?.userId,
        firstName: data?.firstName,
        lastName: data?.lastName,
        emailId: data?.emailId,
        currentAddress1: data?.currentAddress1,
        City: data?.city,
        State: data?.state,
        Zip: data?.zip,
        phoneNumber: data?.phoneNumber,
        companyId,
        userTypeId,
        roleType: data?.roleType,
      }
      createUser(payLoad)
        .then((res: any) => {
          toast({
            id: "createUserToast",
            subTitle: res?.description ?? 'User created successfully',
          })
        })
        .catch((error: any) => {
          reportException(error)
        })
        .finally(() => {
          setBtnLoader(false)
          setShowTemplateForm(false)
          callUserList()
        })
    }

    const BladeContent = (_props: IBladeContentProps) => {
      return (
        <Container className={styles.bladeBodyContainer}>
          <FormProvider {...methods}>
            <InputFormField
              id="firstName"
              name="firstName"
              label={strings.FIRST_NAME}
              maxLength={128}
              required
            />
            <InputFormField
              id={"lastName"}
              name="lastName"
              label={strings.LAST_NAME}
              maxLength={128}
              required
            />
            <InputFormField
              id="emailId"
              name="emailId"
              label={strings.EMAIL}
              maxLength={128}
              validate={emailValidator}
              required
            />
            <InputFormField
              id={"phoneNumber"}
              type="number"
              name="phoneNumber"
              label={strings.MOBILE_NUMBER}
              maxLength={12}
            />
            <InputFormField
              id={"currentAddress1"}
              name="currentAddress1"
              label={strings.ADDRESS}
              maxLength={128}
            />
            <DropdownFormField
              id={"state"}
              name="state"
              label={strings.STATE}
              options={dropdownOptions?.stateOptions}
            />
            <InputFormField
              id={"city"}
              name="city"
              label={strings.CITY}
              maxLength={30}
            />
            <InputFormField
              id={"zipcode"}
              name="zip"
              label={strings.ZIP}
              maxLength={8}
            />
            {isRBACEnabled() && !isEditForm && (
              <DropdownFormField
                id="roleType"
                name="roleType"
                label="Role"
                options={getRoleOptions()}
                required
              />
            )}
          </FormProvider>
        </Container>
      )
    }

    return (
      <Container className={styles.emailTemplateContainer}>
        <HeaderBar
          title={t.USER_MANAGEMENT}
          {...(!isViewOnly() && {
            primaryBtnProps: {
              label: t.CREATE_USER,
              'data-testid': 'create-user-btn',
              onClick: () => {
                reset(isRBACEnabled() ? { roleType: ROLE_TYPES.ADMIN } : {})
                setIsEditForm(false)
                setShowTemplateForm(true)
              },
            },
          })}
        />
        <DataGridTable
          title={t.USERS}
          loading={loading}
          data={userListData || []}
          columns={emailTemplateColumn}
          columnVisibilityModel={{userId: false}}
          checkboxSelection={false}
          totalCount={userListData?.length}
          reduceHeight={280}
          actionList={actionList}
          multiSearchFilter={false}
          getRowId={(row: any) => row?.userId}
          handleActions={handleActions}
          handleCheckboxSelection={handleCheckboxSelection}
          handleSelectionActions={handleSelectionActions}
          handlePagination={(pageNumber, pageSize) =>
            loadPayments(pageNumber, pageSize)
          }
        />
        <Blade
          open={showTemplateForm}
          title={isEditForm ? t.EDIT_USER : t.CREATE_USER}
          onClose={handleClose}
          bodyNode={<BladeContent />}
          footerBtns={{
            primary: {
              id: "createOrUpdateUserBladeConfirmation",
              label: isEditForm ? strings.UPDATE : strings.SAVE,
              onClick: handleSubmit(onSubmit),
              loading: btnLoader,
              disabled: btnLoader,
            },
            secondary: {
              id: "closeUserCreationOrUpdateBlade",
              label: 'Close',
              variant: 'outlined',
              onClick: handleClose,
            },
          }}
        />
        <Blade
          open={showRoleForm}
          title="Switch Role"
          onClose={handleRoleFormClose}
          bodyNode={
            <Container className={styles.bladeBodyContainer}>
              <FormProvider {...methods}>
                <DropdownFormField
                  id="roleType"
                  name="roleType"
                  label="Role"
                  options={getRoleOptions()}
                  required
                />
              </FormProvider>
            </Container>
          }
          footerBtns={{
            primary: {
              id: 'switchRoleBladeConfirmation',
              label: strings.SAVE,
              onClick: handleSubmit(onRoleSubmit),
              loading: btnLoader,
              disabled: btnLoader,
            },
            secondary: {
              id: 'closeSwitchRoleBlade',
              label: 'Close',
              variant: 'outlined',
              onClick: handleRoleFormClose,
            },
          }}
        />
        <SubmitDialog
            cancelId="cancelDeleteUser"
          type="delete"
          open={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          title="Delete Confirmation"
          body1={`Are you sure, you want to delete this user with ID: ${selectedUser}?`}
          body2="Once deleted no longer it will be available"
          primaryBtnProps={{
              id:"confirmDelete",
            label: 'Yes, I confirm',
            loading: btnLoader,
            onClick: handleUserDelete,
            disabled: btnLoader
          }}
        />
      </Container>
    )
  }

export default UserManagementList
